import { Link } from 'react-router';
import { Button, ThemeToggle } from '@mieweb/ui';
import { FAQSection } from '../components/FAQSection';
import type { FAQItem } from '../components/FAQSection';
import { useAuth } from '../lib/auth-context';
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  AnimatePresence,
  type Variants,
} from 'motion/react';
import { useRef, useState, useEffect } from 'react';
import { BrandMark } from '../components/BrandMark';
import { UserAvatar } from '../components/UserAvatar';
import { HeroDownloadLink } from '../components/HeroDownloadLink';
import { isDesktop } from '../lib/desktop';

/* ------------------------------------------------------------------ */
/*  Animation variants                                                 */
/* ------------------------------------------------------------------ */

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: (i: number = 0) => ({
    opacity: 1,
    transition: { duration: 0.5, delay: i * 0.08 },
  }),
};

const scaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: (i: number = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function useAnimateInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: threshold });
  return { ref, inView };
}

function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return prefersReduced;
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const prefersReduced = usePrefersReducedMotion();
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    if (prefersReduced) { setCount(target); return; }
    let cancelled = false;
    const duration = 1600;
    const startTime = performance.now();
    const step = (now: number) => {
      if (cancelled) return;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    return () => { cancelled = true; };
  }, [inView, target, prefersReduced]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const landingFAQs: FAQItem[] = [
  {
    question: 'What is Pollenate?',
    answer: 'Pollenate is an embeddable feedback platform that lets you collect user feedback \u2014 ratings, comments, NPS, CSAT, and more \u2014 directly from your website, app, or documentation. It provides a real-time dashboard to organize, search, and act on the insights you gather.',
  },
  {
    question: 'How do I add Pollenate to my website?',
    answer: 'Just add a single script tag to your HTML with your inbox key and API key. The widget appears automatically. No build step, no complex setup \u2014 it works with any website or framework including React, Next.js, Vue, and static HTML.',
  },
  {
    question: 'Is there a free plan?',
    answer: 'Yes! The Starter plan is free forever and includes 1 inbox, 5,000 events per month, basic analytics, and brandable widgets. No credit card is required to get started.',
  },
  {
    question: 'What types of feedback can I collect?',
    answer: 'Pollenate supports thumbs up/down, star ratings, NPS (Net Promoter Score), CSAT (Customer Satisfaction), emoji reactions, and open-ended text feedback via widgets. Feedback Pages expand this further with 18 question types including email, phone, number, date, dropdown selects, single & multi-choice, drag-and-drop rank-order questions, file uploads, and in-browser voice recordings, plus layout controls like content blocks and section headers. All input types include built-in validation.',
  },
  {
    question: 'What are Feedback Pages?',
    answer: 'Feedback Pages are standalone, branded feedback forms you build with a drag-and-drop editor. Add multiple question types (star ratings, NPS, text, multiple choice, and more), customize colors and branding, then share via a direct link, QR code, email signature button, embeddable iframe, or a print-ready flyer. Responses flow into your dashboard with built-in analytics including conversion rates, daily trends, and per-question breakdowns.',
  },
  {
    question: 'Can I customize the look of the widget?',
    answer: 'Absolutely. Pollenate widgets are fully themeable via JSON theme tokens. You can match your brand colors, fonts, and styling. The widget also supports automatic dark mode.',
  },
  {
    question: 'Is Pollenate HIPAA-compliant?',
    answer: 'Pollenate is built with a privacy-first, HIPAA-ready architecture. Enterprise plans include a Business Associate Agreement (BAA), field-level encryption options, and data residency controls for regulated industries.',
  },
  {
    question: 'What is semantic search?',
    answer: 'Semantic search lets you search your feedback using natural language instead of exact keywords. Powered by AI embeddings, it understands meaning \u2014 so searching \u201cusers frustrated with checkout\u201d finds relevant feedback even if those exact words were never used.',
  },
  {
    question: 'Can multiple team members access the dashboard?',
    answer: 'Yes. Pollenate has a multi-tenant architecture with team collaboration built in. You can invite team members, manage roles, and share inbox access across your organization.',
  },
];

const features = [
  { icon: '\u26A1', title: 'Drop-in Embeds', description: 'Add feedback collection to any site with a single HTML tag. No complex setup required.' },
  { icon: '\uD83D\uDCCB', title: 'Feedback Pages', description: 'Build branded, standalone feedback forms with a drag-and-drop editor. Share via link, QR code, or embed.' },
  { icon: '\uD83E\uDDF1', title: 'React & Web Components', description: 'Works everywhere \u2014 from SPAs to static docs. Use our React package or vanilla JS.' },
  { icon: '\uD83D\uDCDC', title: 'OpenAPI-first', description: 'Unified schema across SDKs, UI, and collector. Full API documentation included.' },
  { icon: '\uD83C\uDFA8', title: 'Fully Themeable', description: 'Design once, share styles via JSON. Match your brand perfectly with theme tokens.' },
  { icon: '\uD83D\uDD10', title: 'Privacy-first', description: 'Built with security in mind. HIPAA-ready with optional field-level encryption.' },
  { icon: '\uD83D\uDEF0\uFE0F', title: 'Realtime Dashboards', description: 'Watch feedback stream in live. Never miss an insight with instant updates.' },
  { icon: '\uD83D\uDD0D', title: 'Semantic Search', description: 'Search feedback using natural language with AI-powered vector similarity.' },
  { icon: '\uD83D\uDC65', title: 'Team Collaboration', description: 'Multi-tenant architecture with role-based access control and team invites.' },
  { icon: '\u2601\uFE0F', title: 'Global Edge Network', description: 'Runs on our worldwide edge network for ultra low-latency and 99.9% uptime.' },
];

const steps = [
  { step: '01', title: 'Widget', subtitle: 'Collect Anywhere', description: 'Drop our customizable widget into any website, app, or documentation. Supports thumbs, stars, NPS, CSAT, emoji, and text feedback.', gradient: 'from-amber-500 to-orange-500' },
  { step: '02', title: 'Collector', subtitle: 'Store Securely', description: 'Our serverless backend validates, enriches, and stores feedback. Deployed across our global edge network for instant response times.', gradient: 'from-orange-500 to-rose-500' },
  { step: '03', title: 'Dashboard', subtitle: 'Act on Insights', description: 'View real-time feedback streams, analyze trends, search with AI, and export data. Collaborate with your entire team.', gradient: 'from-rose-500 to-pink-500' },
];

const plans = [
  { name: 'Starter', monthlyPrice: '$0', annualPrice: '$0', period: 'forever', annualPeriod: 'forever', description: 'Perfect for side projects and testing', features: ['1 inbox', '5,000 events/month', 'Basic analytics', 'Community support', 'Brandable widgets'], cta: 'Get Started Free', popular: false },
  { name: 'Team', monthlyPrice: '$49', annualPrice: '$39', period: '/month', annualPeriod: '/month', description: 'For growing teams that need more', features: ['5 inboxes', '100,000 events/month', 'Advanced analytics', 'Webhook integrations', 'Priority support', 'Team collaboration', 'Custom themes'], cta: 'Start Free Trial', popular: true },
  { name: 'Enterprise', monthlyPrice: 'Custom', annualPrice: 'Custom', period: '', annualPeriod: '', description: 'For large organizations with specific needs', features: ['Unlimited inboxes', 'Unlimited events', 'SSO / SAML', 'BAA available', 'SLA guarantee', 'Dedicated support', 'Custom integrations'], cta: 'Contact Sales', popular: false },
];

const logos = [
  { name: 'BlueHive', url: 'https://bluehive.com', img: '/logos/bluehive.png' },
  { name: 'Ozwell', url: 'https://ozwell.ai', img: '/logos/ozwell.png' },
  { name: 'Enterprise Health', url: 'https://enterprisehealth.com', img: '/logos/enterprise-health.png' },
  { name: 'WebChart', url: 'https://webchartnow.com', img: '/logos/webchart.png' },
];

const emojis = ['\uD83D\uDE1E', '\uD83D\uDE10', '\uD83D\uDE42', '\uD83D\uDE04', '\uD83E\uDD29'];

const widgetTypes = [
  { key: 'emoji', label: 'How was your experience?' },
  { key: 'stars', label: 'Rate your experience' },
  { key: 'thumbs', label: 'Was this helpful?' },
  { key: 'nps', label: 'How likely are you to recommend us?' },
  { key: 'csat', label: 'How satisfied are you?' },
  { key: 'text', label: 'Share your feedback' },
] as const;

/* ------------------------------------------------------------------ */
/*  LandingPage                                                        */
/* ------------------------------------------------------------------ */

export function LandingPage() {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Inject Organization + SoftwareApplication JSON-LD for SEO
  useEffect(() => {
    const scriptId = 'ld-json-org';
    document.getElementById(scriptId)?.remove();
    const ldJson = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Pollenate',
        url: 'https://pollenate.dev',
        description: 'Embeddable customer feedback platform — collect ratings, NPS, CSAT, and survey responses with drop-in widgets and feedback pages, then analyze them with AI.',
        publisher: {
          '@type': 'Organization',
          name: 'Pollenate',
          url: 'https://pollenate.dev',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Pollenate',
        url: 'https://pollenate.dev',
        logo: 'https://pollenate.dev/favicon-light.svg',
        sameAs: [
          'https://x.com/PollenateCloud',
          'https://linkedin.com/company/pollenate-feedback/',
        ],
        parentOrganization: {
          '@type': 'Organization',
          name: 'BlueHive Health',
          url: 'https://bluehive.com',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Pollenate',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://pollenate.dev',
        offers: [
          {
            '@type': 'Offer',
            name: 'Starter',
            price: '0',
            priceCurrency: 'USD',
            description: 'Perfect for side projects and testing',
          },
          {
            '@type': 'Offer',
            name: 'Team',
            price: '49',
            priceCurrency: 'USD',
            billingIncrement: 'P1M',
            description: 'For growing teams that need more',
          },
        ],
        description: 'Collect, analyze, and act on customer feedback with embeddable widgets, AI-powered insights, and developer-friendly APIs.',
      },
    ];
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ldJson);
    document.head.appendChild(script);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [selectedEmoji, setSelectedEmoji] = useState(4);
  const [selectedStars, setSelectedStars] = useState(4);
  const [selectedThumb, setSelectedThumb] = useState<number | null>(1);
  const [selectedNps, setSelectedNps] = useState(9);
  const [selectedCsat, setSelectedCsat] = useState(4);
  const [activeWidgetIndex, setActiveWidgetIndex] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false);

  // Cycle through widget types every 4 seconds (paused if user prefers reduced motion)
  const prefersReducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      setActiveWidgetIndex((prev) => (prev + 1) % widgetTypes.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  const featuresAnim = useAnimateInView(0.1);
  const stepsAnim = useAnimateInView(0.15);
  const codeAnim = useAnimateInView(0.2);
  const feedbackPagesAnim = useAnimateInView(0.15);
  const aiAgentsAnim = useAnimateInView(0.15);
  const pricingAnim = useAnimateInView(0.1);
  const ctaAnim = useAnimateInView(0.2);
  const socialAnim = useAnimateInView(0.3);
  const statsAnim = useAnimateInView(0.3);
  const activeWidget = widgetTypes[activeWidgetIndex] ?? widgetTypes[0];

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950 overflow-x-hidden">
      {/* Skip to content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-amber-500 focus:text-white focus:rounded-lg focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* ---- Navigation ---- */}
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/70 dark:bg-neutral-950/70 backdrop-blur-xl border-b border-slate-200/60 dark:border-neutral-800/60"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5 group">
              <motion.span
                whileHover={{ rotate: 20, scale: 1.15 }}
                transition={{ type: 'spring', stiffness: 400 }}
                className="inline-flex"
              >
                <BrandMark className="h-8 w-8" />
              </motion.span>
              <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Pollenate</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {['Features', 'Feedback Pages', 'Developers', 'Pricing', 'FAQ', 'Docs', ...(isDesktop ? [] : ['Download'])].map((label) => {
                const href =
                  label === 'Docs'
                    ? '/docs'
                    : label === 'Download'
                      ? '/download'
                      : label === 'Developers'
                        ? '/developers'
                        : `#${label.toLowerCase().replace(/\s+/g, '-')}`;
                return (
                  <a key={label} href={href} className="relative text-sm font-medium text-slate-500 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors group">
                    {label}
                    <span className="absolute -bottom-0.5 left-0 w-0 h-[2px] bg-gradient-to-r from-amber-500 to-orange-500 group-hover:w-full transition-all duration-300" />
                  </a>
                );
              })}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              {!isLoading && isAuthenticated && user ? (
                <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3">
                  <UserAvatar
                    email={user.email}
                    name={user.name}
                    avatarUrl={user.avatarUrl}
                    className="h-8 w-8 text-sm ring-2 ring-amber-200"
                  />
                  <Button>Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" className="text-sm hidden sm:inline-flex">Sign In</Button></Link>
                  <Link to="/login"><Button variant="ghost" className="text-xs sm:hidden px-1 whitespace-nowrap">Sign In</Button></Link>
                  <Link to="/login?mode=signup">
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                      <Button className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">Get Started</Button>
                    </motion.div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.nav>

      {/* ---- Hero ---- */}
      <section id="main-content" ref={heroRef} className="relative pt-28 pb-8 sm:pt-36 sm:pb-12 lg:pt-44 lg:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-amber-200/40 via-orange-100/30 to-transparent blur-3xl" />
          <div className="absolute -bottom-60 -left-40 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-rose-100/30 via-pink-50/20 to-transparent blur-3xl" />
        </div>

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-50 dark:bg-amber-900/60 border border-amber-200/60 dark:border-amber-600/50 text-amber-700 dark:text-amber-300 rounded-full text-sm font-medium mb-8 shadow-sm"
          >
            <motion.span animate={{ rotate: [0, 14, -14, 0] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}>{'\uD83C\uDF89'}</motion.span>
            <span>Now in public beta &mdash; free for early adopters</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl lg:text-8xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-[1.08] mb-6"
          >
            Let feedback spread.
            <br />
            <motion.span
              className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent inline-block"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              style={{ backgroundSize: '200% 200%' }}
            >
              Let ideas grow.
            </motion.span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-lg sm:text-xl lg:text-2xl text-slate-500 dark:text-neutral-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            The AI-native feedback platform with a developer-first API &mdash; collect from your app, docs, or AI agents, search it by meaning, and act on what matters.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.45 }} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 max-w-md sm:max-w-none mx-auto">
            <Link to="/login?mode=signup">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button size="lg" className="w-full sm:w-auto text-base whitespace-nowrap px-8 py-5 shadow-lg shadow-amber-500/20">
                  Start Collecting Feedback
                  <motion.span className="inline-block ml-2" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2 }}>{'\u2192'}</motion.span>
                </Button>
              </motion.div>
            </Link>
            <a href="/docs">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-base whitespace-nowrap px-8 py-5">View Documentation</Button>
              </motion.div>
            </a>
          </motion.div>

          <HeroDownloadLink />

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 text-sm text-slate-400 dark:text-neutral-500">
            No credit card required&ensp;&middot;&ensp;Free tier available forever
          </motion.p>
        </motion.div>
      </section>

      {/* ---- Widget Preview ---- */}
      <section className="pb-16 pt-4 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl mx-auto"
        >
          <div className="relative rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-2 sm:p-3 shadow-2xl shadow-slate-900/20 ring-1 ring-white/10">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-rose-500/20 rounded-[28px] blur-xl opacity-60" />
            <div className="relative rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex-1 ml-3 h-7 rounded-lg bg-slate-700/50 flex items-center px-3">
                  <span className="text-slate-400 text-xs font-mono">your-website.com</span>
                </div>
              </div>

              <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 sm:p-8 shadow-inner">
                <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-0 sm:justify-between">
                  <div className="flex-1">
                    <div className="h-5 w-52 bg-slate-200 dark:bg-neutral-700 rounded-md mb-5 animate-pulse" />
                    <div className="space-y-2.5">
                      <div className="h-3 w-full bg-slate-100 dark:bg-neutral-700/60 rounded animate-pulse" />
                      <div className="h-3 w-5/6 bg-slate-100 dark:bg-neutral-700/60 rounded animate-pulse" />
                      <div className="h-3 w-3/4 bg-slate-100 dark:bg-neutral-700/60 rounded animate-pulse" />
                      <div className="h-3 w-2/3 bg-slate-100 dark:bg-neutral-700/60 rounded animate-pulse" />
                    </div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 }}
                    className="sm:ml-8 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-amber-950/40 rounded-2xl p-6 border border-amber-200/60 dark:border-amber-700/40 shadow-xl shadow-amber-500/10 w-full sm:min-w-[280px] sm:max-w-[340px] h-[340px] relative overflow-hidden"
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeWidget.key}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className="text-slate-700 dark:text-neutral-200 font-semibold mb-4 text-sm">{activeWidget.label}</p>

                        {/* Emoji */}
                        {activeWidget.key === 'emoji' && (
                          <div className="flex gap-2 mb-4">
                            {emojis.map((emoji, i) => (
                              <motion.button
                                key={i}
                                whileHover={{ scale: 1.2, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                animate={selectedEmoji === i ? { scale: 1.15 } : { scale: 1 }}
                                onClick={() => setSelectedEmoji(i)}
                                className={`w-10 h-10 rounded-xl transition-colors ${selectedEmoji === i ? 'bg-amber-500 shadow-lg shadow-amber-500/30' : 'bg-white dark:bg-neutral-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-slate-100 dark:border-neutral-700'} flex items-center justify-center text-xl cursor-pointer`}
                              >
                                {emoji}
                              </motion.button>
                            ))}
                          </div>
                        )}

                        {/* Stars */}
                        {activeWidget.key === 'stars' && (
                          <div className="flex gap-1 mb-4">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <motion.button
                                key={i}
                                whileHover={{ scale: 1.2, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setSelectedStars(i)}
                                className={`w-10 h-10 rounded-xl transition-colors ${i <= selectedStars ? 'bg-amber-500 shadow-lg shadow-amber-500/30 text-white' : 'bg-white dark:bg-neutral-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-slate-100 dark:border-neutral-700 text-amber-400'} flex items-center justify-center text-xl cursor-pointer`}
                              >
                                ★
                              </motion.button>
                            ))}
                          </div>
                        )}

                        {/* Thumbs */}
                        {activeWidget.key === 'thumbs' && (
                          <div className="flex gap-3 mb-4">
                            {[{ val: 0, emoji: '👎' }, { val: 1, emoji: '👍' }].map((t) => (
                              <motion.button
                                key={t.val}
                                whileHover={{ scale: 1.15, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setSelectedThumb(t.val)}
                                className={`w-14 h-14 rounded-xl transition-colors ${selectedThumb === t.val ? 'bg-amber-500 shadow-lg shadow-amber-500/30' : 'bg-white dark:bg-neutral-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-slate-100 dark:border-neutral-700'} flex items-center justify-center text-2xl cursor-pointer`}
                              >
                                {t.emoji}
                              </motion.button>
                            ))}
                          </div>
                        )}

                        {/* NPS (0-10) */}
                        {activeWidget.key === 'nps' && (
                          <div className="grid grid-cols-11 gap-1 mb-4">
                            {Array.from({ length: 11 }, (_, i) => (
                              <motion.button
                                key={i}
                                whileHover={{ scale: 1.15 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setSelectedNps(i)}
                                className={`h-8 rounded-lg text-xs font-semibold transition-colors ${
                                  selectedNps === i
                                    ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                                    : i <= 6 ? 'bg-red-50 dark:bg-red-950/30 text-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 border border-red-100 dark:border-red-900/40'
                                    : i <= 8 ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-950/50 border border-amber-100 dark:border-amber-900/40'
                                    : 'bg-green-50 dark:bg-green-950/30 text-green-500 hover:bg-green-100 dark:hover:bg-green-950/50 border border-green-100 dark:border-green-900/40'
                                } flex items-center justify-center cursor-pointer`}
                              >
                                {i}
                              </motion.button>
                            ))}
                            <div className="col-span-11 flex justify-between text-[10px] text-slate-400 dark:text-neutral-500 mt-0.5 px-0.5">
                              <span>Not likely</span>
                              <span>Very likely</span>
                            </div>
                          </div>
                        )}

                        {/* CSAT */}
                        {activeWidget.key === 'csat' && (
                          <div className="flex gap-2 mb-4">
                            {['Very\nUnsatisfied', 'Unsatisfied', 'Neutral', 'Satisfied', 'Very\nSatisfied'].map((_label, i) => (
                              <motion.button
                                key={i}
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setSelectedCsat(i)}
                                className={`flex-1 h-10 rounded-xl text-[10px] leading-tight font-medium transition-colors ${selectedCsat === i ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' : 'bg-white dark:bg-neutral-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-slate-100 dark:border-neutral-700 text-slate-500 dark:text-neutral-400'} flex items-center justify-center cursor-pointer`}
                              >
                                {i + 1}
                              </motion.button>
                            ))}
                            <div />
                          </div>
                        )}

                        {/* Text */}
                        {activeWidget.key === 'text' && (
                          <div className="mb-1" />
                        )}

                        <textarea placeholder="Tell us more (optional)" className="w-full p-3 border border-slate-200 dark:border-neutral-700 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/50 bg-white/80 dark:bg-neutral-800 text-slate-900 dark:text-neutral-200 placeholder:text-slate-400 dark:placeholder:text-neutral-500" rows={2} readOnly />
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-2.5 px-4 rounded-xl transition-all shadow-md shadow-amber-500/20">
                          Send Feedback
                        </motion.button>
                      </motion.div>
                    </AnimatePresence>

                    {/* Dot indicators */}
                    <div className="flex justify-center gap-1.5 mt-4" role="tablist" aria-label="Widget type selector">
                      {widgetTypes.map((wt, i) => (
                        <button
                          key={i}
                          role="tab"
                          aria-selected={i === activeWidgetIndex}
                          aria-label={`Show ${wt.label} widget`}
                          onClick={() => setActiveWidgetIndex(i)}
                          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                            i === activeWidgetIndex ? 'bg-amber-500 w-4' : 'bg-slate-300 dark:bg-neutral-600 hover:bg-slate-400 dark:hover:bg-neutral-500'
                          }`}
                        />
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ---- Social Proof ---- */}
      <section ref={socialAnim.ref} className="py-14 px-4 sm:px-6 lg:px-8 border-y border-slate-200/60 dark:border-neutral-800/60 bg-slate-50/80 dark:bg-neutral-950">
        <div className="max-w-7xl mx-auto">
          <motion.p variants={fadeIn} initial="hidden" animate={socialAnim.inView ? 'visible' : 'hidden'} className="text-center text-sm text-slate-400 dark:text-neutral-500 uppercase tracking-widest font-medium mb-10">
            Trusted by teams building exceptional products
          </motion.p>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {logos.map((logo, i) => (
              <motion.a key={logo.name} href={logo.url} target="_blank" rel="noopener noreferrer" variants={fadeIn} initial="hidden" animate={socialAnim.inView ? 'visible' : 'hidden'} custom={i} whileHover={{ scale: 1.08 }} className="group opacity-60 hover:opacity-100 transition-all duration-300 rounded-xl px-4 py-2 -mx-4 -my-2 dark:hover:bg-white/10">
                <img src={logo.img} alt={logo.name} loading="lazy" width="120" height="36" className="h-9 w-auto object-contain grayscale group-hover:grayscale-0 dark:invert dark:group-hover:invert-0 transition-all duration-300" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Stats ---- */}
      <section ref={statsAnim.ref} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={statsAnim.inView ? 'visible' : 'hidden'} className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: 99.9, suffix: '%', label: 'Uptime SLA' },
              { value: 50, suffix: 'ms', label: 'Avg. Response' },
              { value: 16, suffix: '', label: 'Question Types' },
              { value: 200, suffix: '+', label: 'Edge Locations' },
            ].map((stat, i) => (
              <motion.div key={stat.label} variants={fadeUp} custom={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mb-1">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-slate-500 dark:text-neutral-400 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---- Features ---- */}
      <section id="features" ref={featuresAnim.ref} className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={featuresAnim.inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">Features</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Everything you need to understand your users</h2>
            <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto">Pollenate gives you the tools to collect meaningful feedback and turn it into actionable insights.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, i) => (
              <motion.div key={feature.title} variants={scaleUp} initial="hidden" animate={featuresAnim.inView ? 'visible' : 'hidden'} custom={i} whileHover={{ y: -6, transition: { duration: 0.2 } }} className="group relative p-7 rounded-2xl border border-slate-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:shadow-xl hover:shadow-amber-500/5 hover:border-amber-200/80 dark:hover:border-amber-700/50 transition-all duration-300">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-50/0 to-orange-50/0 group-hover:from-amber-50/50 group-hover:to-orange-50/30 dark:group-hover:from-amber-950/20 dark:group-hover:to-orange-950/10 transition-all duration-300" />
                <div className="relative">
                  <motion.div whileHover={{ scale: 1.15, rotate: 5 }} className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center text-2xl mb-4 shadow-sm">
                    {feature.icon}
                  </motion.div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-500 dark:text-neutral-400 text-[15px] leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- How it Works ---- */}
      <section id="how-it-works" ref={stepsAnim.ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 dark:from-neutral-900 to-white dark:to-neutral-950">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={stepsAnim.inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">How it works</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Three layers, infinite possibilities</h2>
            <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto">Pollenate works as a complete feedback rail for your entire ecosystem.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-[16.67%] right-[16.67%] h-0.5 bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 opacity-40" />
            {steps.map((item, i) => (
              <motion.div key={item.step} variants={fadeUp} initial="hidden" animate={stepsAnim.inView ? 'visible' : 'hidden'} custom={i} className="relative">
                <motion.div whileHover={{ scale: 1.05, rotate: 2 }} className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-bold text-xl shadow-lg mb-6`}>
                  {item.step}
                </motion.div>
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-8 border border-slate-200/80 dark:border-neutral-800 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">{item.title}</h3>
                  <p className="text-amber-600 dark:text-amber-400 font-semibold text-sm mb-4">{item.subtitle}</p>
                  <p className="text-slate-500 dark:text-neutral-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Code Example ---- */}
      <section ref={codeAnim.ref} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div variants={slideInLeft} initial="hidden" animate={codeAnim.inView ? 'visible' : 'hidden'}>
              <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">Integration</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Add feedback in minutes, not hours</h2>
              <p className="text-lg text-slate-500 dark:text-neutral-400 mb-8 sm:mb-10 leading-relaxed">Just two lines of code to start collecting feedback. Works with any website or framework.</p>
              <div className="space-y-5">
                {['Automatic dark mode support', 'Customizable themes and positioning', 'Event callbacks for analytics', 'Zero dependencies, tiny footprint'].map((item, i) => (
                  <motion.div key={item} variants={fadeUp} initial="hidden" animate={codeAnim.inView ? 'visible' : 'hidden'} custom={i + 2} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-slate-600 dark:text-neutral-300 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={slideInRight} initial="hidden" animate={codeAnim.inView ? 'visible' : 'hidden'} className="relative min-w-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-slate-900 rounded-2xl p-4 sm:p-6 shadow-2xl ring-1 ring-white/10 overflow-hidden">
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="ml-3 text-slate-500 text-xs font-mono">index.html</span>
                </div>
                <pre className="text-xs sm:text-sm overflow-x-auto leading-relaxed">
                  <code className="text-slate-300">
                    <span className="text-slate-500">{`<!-- Add the widget script -->`}</span>
                    {'\n'}
                    <span className="text-pink-400">{`<script`}</span>
                    <span className="text-amber-300">{` src`}</span>
                    <span className="text-slate-400">=</span>
                    <span className="text-green-400">"https://pollenate.dev/widget.js"</span>
                    <span className="text-pink-400">{`>`}</span>
                    <span className="text-pink-400">{`</script>`}</span>
                    {'\n\n'}
                    <span className="text-slate-500">{`<!-- Initialize with your inbox key -->`}</span>
                    {'\n'}
                    <span className="text-pink-400">{`<script>`}</span>
                    {'\n'}
                    <span className="text-slate-300">{'  Pollenate.'}</span>
                    <span className="text-amber-300">init</span>
                    <span className="text-slate-300">{'({'}</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">inboxKey</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">'your-inbox-key'</span>
                    {'\n'}
                    <span className="text-slate-300">{'  });'}</span>
                    {'\n'}
                    <span className="text-pink-400">{`</script>`}</span>
                  </code>
                </pre>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- Feedback Pages ---- */}
      <section id="feedback-pages" ref={feedbackPagesAnim.ref} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white dark:from-neutral-950 to-slate-50 dark:to-neutral-900 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">Feedback Pages</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Build branded feedback forms in minutes</h2>
            <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto">Create standalone, multi-question feedback pages with a visual editor. Share anywhere &mdash; no code required.</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Visual mockup */}
            <motion.div variants={slideInLeft} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'} className="relative min-w-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-3xl blur-2xl pointer-events-none" />
              <div className="relative bg-white dark:bg-neutral-900 rounded-2xl p-6 sm:p-8 shadow-2xl ring-1 ring-slate-200/60 dark:ring-neutral-800">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Patient Satisfaction Survey</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">Published</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-neutral-800 text-slate-600 dark:text-neutral-400">Preview</div>
                    <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500 text-white">Save</div>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { num: 1, type: 'Star Rating', label: 'How would you rate your experience?', badge: 'Required', icon: '⭐' },
                    { num: 2, type: 'Text', label: 'Any additional comments?', icon: '💬' },
                    { num: 3, type: 'Multiple Choice', label: 'How did you hear about us?', icon: '☑️', options: ['Search', 'Referral', 'Social Media'] },
                    { num: 4, type: 'Rank Order', label: 'Rank these features by importance', icon: '↕️', options: ['Speed', 'Support', 'Price'] },
                  ].map((q, i) => (
                    <motion.div key={q.num} variants={fadeUp} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'} custom={i + 2} className="rounded-xl border border-slate-200 dark:border-neutral-800 p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-lg">{q.icon}</span>
                        <span className="text-xs text-slate-400 dark:text-neutral-500 font-medium">Q{q.num}</span>
                        <span className="px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 text-xs rounded-full">{q.type}</span>
                        {q.badge && <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded-full">{q.badge}</span>}
                      </div>
                      <p className="text-sm font-medium text-slate-700 dark:text-neutral-300">{q.label}</p>
                      {q.options && (
                        <div className="flex gap-1.5 mt-2">
                          {q.options.map(opt => (
                            <span key={opt} className="px-2 py-0.5 bg-slate-100 dark:bg-neutral-800 rounded text-xs text-slate-500 dark:text-neutral-400">{opt}</span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                  <div className="rounded-xl border-2 border-dashed border-slate-200 dark:border-neutral-700 p-3 text-center text-sm text-slate-400 dark:text-neutral-500">+ Add Question</div>
                </div>
              </div>
            </motion.div>

            {/* Features list */}
            <motion.div variants={slideInRight} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'}>
              <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">No-code Form Builder</span>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Everything you need for surveys &amp; feedback forms</h3>
              <p className="text-lg text-slate-500 dark:text-neutral-400 mb-8 leading-relaxed">Design multi-question forms, customize branding, and share instantly. Perfect for patient surveys, event feedback, product reviews, and more.</p>

              <div className="space-y-5">
                {[
                  { icon: '🛠️', title: '18 question types', desc: 'Ratings, NPS, CSAT, text, choice, email, phone, number, date, dropdown, rank order, file upload, voice recording, and layout controls' },
                  { icon: '🎨', title: 'Brand customization', desc: 'Custom colors, logos, thank-you messages, and redirect URLs' },
                  { icon: '📱', title: 'Share anywhere', desc: 'Direct link, QR code, email signature, iframe embed, or print flyer' },
                  { icon: '📊', title: 'Built-in analytics', desc: 'Response trends, conversion rates, and per-question score breakdowns' },
                  { icon: '🔒', title: 'Submission controls', desc: 'Anonymous mode, contact info requirements, and single-submission limits' },
                  { icon: '🤖', title: 'Bot protection', desc: 'Built-in Turnstile captcha to prevent spam submissions' },
                ].map((item, i) => (
                  <motion.div key={item.title} variants={fadeUp} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'} custom={i + 2} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center text-xl flex-shrink-0">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-0.5">{item.title}</h4>
                      <p className="text-slate-500 dark:text-neutral-400 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: item.desc }} />
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeUp} initial="hidden" animate={feedbackPagesAnim.inView ? 'visible' : 'hidden'} custom={9} className="mt-8">
                <Link to="/login?mode=signup">
                  <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                    <Button className="text-base px-6 py-4 shadow-lg shadow-amber-500/20">
                      Create Your First Form
                      <motion.span className="inline-block ml-2" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2 }}>{'\u2192'}</motion.span>
                    </Button>
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- AI Agent Integration ---- */}
      <section id="ai-agents" ref={aiAgentsAnim.ref} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 dark:from-neutral-900 to-white dark:to-neutral-950 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={aiAgentsAnim.inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">AI-Native Feedback</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Built for AI agents, too</h2>
            <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto">AI agents can submit feedback during conversations with users &mdash; capturing satisfaction, sentiment, and product feedback in real-time.</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div variants={slideInLeft} initial="hidden" animate={aiAgentsAnim.inView ? 'visible' : 'hidden'} className="min-w-0">
              {/* Flow diagram */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-700/40 rounded-2xl p-4 sm:p-6 mb-8">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-4 text-sm uppercase tracking-wider">How It Works</h3>
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-2 sm:gap-3 text-sm text-amber-800 dark:text-amber-300">
                  {['User talks to AI', 'Agent detects sentiment', 'POST /collect', 'Dashboard'].map((label, i) => (
                    <div key={label} className="flex items-center gap-2 sm:gap-3">
                      <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-4 py-1.5 font-medium whitespace-nowrap">{label}</span>
                      {i < 3 && <span className="hidden sm:inline text-amber-400 dark:text-amber-600">{'\u2192'}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Capabilities */}
              <div className="space-y-4">
                {[
                  { icon: '\uD83D\uDCDD', title: 'Skill File Discovery', description: 'Publish skill.md and skill.json files so AI agents can automatically discover and learn your feedback API.' },
                  { icon: '\uD83E\uDDE9', title: 'MCP Server', description: 'Connect Claude Desktop, Cursor, or any MCP client directly to Pollenate via the dedicated /mcp endpoint — submit feedback, query stats, and search without writing HTTP calls.' },
                  { icon: '\uD83E\uDD16', title: 'Dedicated MCP Endpoint', description: 'Agents integrate through the /mcp endpoint, while widgets and SDKs continue to use /collect behind the scenes.' },
                  { icon: '\uD83D\uDD17', title: 'Rich Context Support', description: 'Attach agent name, sentiment, session IDs, and arbitrary metadata to every feedback event.' },
                ].map((item, i) => (
                  <motion.div key={item.title} variants={fadeUp} initial="hidden" animate={aiAgentsAnim.inView ? 'visible' : 'hidden'} custom={i + 2} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center text-xl flex-shrink-0">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-0.5">{item.title}</h4>
                      <p className="text-slate-500 dark:text-neutral-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Code sample */}
            <motion.div variants={slideInRight} initial="hidden" animate={aiAgentsAnim.inView ? 'visible' : 'hidden'} className="relative min-w-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-3xl blur-2xl pointer-events-none" />
              <div className="relative bg-slate-900 rounded-2xl p-4 sm:p-6 shadow-2xl ring-1 ring-white/10 overflow-hidden">
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="ml-3 text-slate-500 text-xs font-mono">agent-feedback.sh</span>
                </div>
                <pre className="text-xs sm:text-sm overflow-x-auto leading-relaxed">
                  <code className="text-slate-300">
                    <span className="text-slate-500"># AI agent submits feedback after a conversation</span>
                    {'\n'}
                    <span className="text-amber-300">curl</span>
                    <span className="text-slate-300">{' -X POST https://api.pollenate.dev/collect \\'}</span>
                    {'\n'}
                    <span className="text-slate-300">{'  -H '}</span>
                    <span className="text-green-400">"Content-Type: application/json"</span>
                    <span className="text-slate-300">{' \\'}</span>
                    {'\n'}
                    <span className="text-slate-300">{'  -H '}</span>
                    <span className="text-green-400">"X-Pollenate-Key: YOUR_API_KEY"</span>
                    <span className="text-slate-300">{' \\'}</span>
                    {'\n'}
                    <span className="text-slate-300">{'  -d '}</span>
                    <span className="text-green-400">{"'"}</span>
                    <span className="text-pink-400">{'{'}</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">"inboxKey"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">"support-chat"</span>
                    <span className="text-slate-300">,</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">"type"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">"thumbs"</span>
                    <span className="text-slate-300">,</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">"score"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-purple-400">1</span>
                    <span className="text-slate-300">,</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">"comment"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">"Issue resolved successfully"</span>
                    <span className="text-slate-300">,</span>
                    {'\n'}
                    <span className="text-slate-300">{'    '}</span>
                    <span className="text-amber-300">"context"</span>
                    <span className="text-slate-300">{': { '}</span>
                    {'\n'}
                    <span className="text-slate-300">{'      '}</span>
                    <span className="text-amber-300">"source"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">"ai-agent"</span>
                    <span className="text-slate-300">,</span>
                    {'\n'}
                    <span className="text-slate-300">{'      '}</span>
                    <span className="text-amber-300">"agentName"</span>
                    <span className="text-slate-300">{': '}</span>
                    <span className="text-green-400">"SupportBot"</span>
                    {'\n'}
                    <span className="text-slate-300">{'    }'}</span>
                    {'\n'}
                    <span className="text-pink-400">{'}'}</span>
                    <span className="text-green-400">{"'"}</span>
                  </code>
                </pre>
              </div>
              <motion.div variants={fadeUp} initial="hidden" animate={aiAgentsAnim.inView ? 'visible' : 'hidden'} custom={5} className="mt-6 text-center">
                <Link to="/docs#ai-agents" className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors group">
                  View full AI agent docs
                  <motion.span className="inline-block group-hover:translate-x-1 transition-transform" animate={{ x: [0, 3, 0] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}>{'\u2192'}</motion.span>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- Pricing ---- */}
      <section id="pricing" ref={pricingAnim.ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 dark:from-neutral-900 to-white dark:to-neutral-950">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate={pricingAnim.inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-amber-600 uppercase tracking-wider mb-3">Pricing</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mb-5 tracking-tight">Simple, transparent pricing</h2>
            <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto">Start free, scale as you grow. No hidden fees.</p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mt-8">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-neutral-500'}`}>Monthly</span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                aria-label={`Switch to ${isAnnual ? 'monthly' : 'annual'} billing`}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${isAnnual ? 'bg-amber-500' : 'bg-slate-300 dark:bg-neutral-700'}`}
              >
                <motion.span
                  layout
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm ${isAnnual ? 'ml-6' : 'ml-1'}`}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-neutral-500'}`}>
                Annual
                <span className="ml-1.5 inline-block px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full">Save 20%</span>
              </span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
            {plans.map((plan, i) => {
              const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
              const period = isAnnual ? plan.annualPeriod : plan.period;
              return (
              <motion.div key={plan.name} variants={scaleUp} initial="hidden" animate={pricingAnim.inView ? 'visible' : 'hidden'} custom={i} whileHover={{ y: -8, transition: { duration: 0.25 } }} className={`relative rounded-3xl p-8 flex flex-col ${plan.popular ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white shadow-2xl shadow-amber-500/20 ring-2 ring-amber-400/30 z-10 lg:scale-105' : 'bg-white dark:bg-neutral-900 border border-slate-200/80 dark:border-neutral-800 shadow-sm'}`}>
                {plan.popular && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="absolute -top-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg">
                    Most Popular
                  </motion.div>
                )}
                <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className={`text-5xl font-extrabold ${plan.popular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{price}</span>
                  <span className={`text-sm ${plan.popular ? 'text-white/70' : 'text-slate-400 dark:text-neutral-500'}`}>{period}</span>
                  {isAnnual && plan.monthlyPrice !== plan.annualPrice && (
                    <span className={`text-sm line-through ml-1 ${plan.popular ? 'text-white/40' : 'text-slate-300 dark:text-neutral-600'}`}>{plan.monthlyPrice}</span>
                  )}
                </div>
                <p className={`mb-8 text-sm ${plan.popular ? 'text-white/80' : 'text-slate-500 dark:text-neutral-400'}`}>{plan.description}</p>
                <ul className="space-y-3.5 mb-8 flex-1">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2.5">
                      <svg className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-green-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      <span className={`text-sm ${plan.popular ? 'text-white/90' : 'text-slate-600 dark:text-neutral-300'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login?mode=signup" className="mt-auto">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant={plan.popular ? 'secondary' : 'primary'} className={`w-full ${plan.popular ? 'bg-white dark:bg-neutral-900 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-neutral-800 shadow-lg' : ''}`}>{plan.cta}</Button>
                  </motion.div>
                </Link>
              </motion.div>
              );
            })}
          </div>

          <motion.p variants={fadeIn} initial="hidden" animate={pricingAnim.inView ? 'visible' : 'hidden'} custom={4} className="text-center mt-10 text-sm text-slate-400 dark:text-neutral-500">
            All plans include SSL, API access, and 99.9% uptime SLA.{isAnnual && ' Billed annually.'}<br />
            Need a custom plan?{' '}<a href="mailto:sales@pollenate.dev" className="text-amber-600 hover:text-amber-700 underline underline-offset-2">Contact our sales team</a>.
          </motion.p>

          {/* Trust badges */}
          <motion.div variants={fadeUp} initial="hidden" animate={pricingAnim.inView ? 'visible' : 'hidden'} custom={5} className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { icon: '🔒', label: 'SSL Encrypted' },
              { icon: '🏥', label: 'HIPAA-Ready' },
              { icon: '🛡️', label: 'GDPR Compliant' },
              { icon: '⏱️', label: '99.9% Uptime SLA' },
            ].map((badge) => (
              <div key={badge.label} className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm text-slate-600 dark:text-neutral-400">
                <span>{badge.icon}</span>
                <span className="font-medium">{badge.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <FAQSection faqs={landingFAQs} title="Frequently Asked Questions" subtitle="Everything you need to know about getting started with Pollenate." id="faq" />

      {/* ---- CTA ---- */}
      <section ref={ctaAnim.ref} className="py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-amber-100/50 via-orange-100/40 to-rose-100/50 rounded-full blur-3xl" />
        </div>
        <motion.div variants={fadeUp} initial="hidden" animate={ctaAnim.inView ? 'visible' : 'hidden'} className="relative max-w-4xl mx-auto text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">Ready to grow your feedback ecosystem?</motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-lg text-slate-500 dark:text-neutral-400 mb-10">Join hundreds of teams using Pollenate to understand their users better.</motion.p>
          <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login?mode=signup">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Button size="lg" className="text-base px-8 py-5 shadow-lg shadow-amber-500/20">
                  Start Free Today
                  <motion.span className="inline-block ml-2" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2 }}>{'\u2192'}</motion.span>
                </Button>
              </motion.div>
            </Link>
            <a href="mailto:sales@pollenate.dev">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Button variant="outline" size="lg" className="text-base px-8 py-5">Talk to Sales</Button>
              </motion.div>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-200/60 dark:border-neutral-800/60 bg-white dark:bg-neutral-950">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 mb-14">
            <div className="lg:w-64 shrink-0">
              <div className="flex items-center gap-2.5 mb-4">
                <BrandMark className="h-8 w-8" />
                <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Pollenate</span>
              </div>
              <p className="text-slate-500 dark:text-neutral-400 text-sm leading-relaxed">Let feedback spread and ideas grow.<br />Built with {'\u2764\uFE0F'} by <a href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 dark:hover:text-white transition-colors">BlueHive Health</a>.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 flex-1">
            {[
              { title: 'Product', links: [{ label: 'Features', href: '#features' }, { label: 'Feedback Pages', href: '#feedback-pages' }, { label: 'Developer API', href: '/developers', internal: true }, { label: 'AI-native feedback', href: '/ai', internal: true }, { label: 'Pricing', href: '/pricing', internal: true }, { label: 'Desktop App', href: '/download', internal: true }, { label: 'API Docs', href: 'https://api.pollenate.dev/docs' }, { label: 'Integrations', href: '#how-it-works' }] },
              { title: 'Solutions', links: [{ label: 'Website feedback widget', href: '/use-cases/website-feedback-widget', internal: true }, { label: 'SaaS & product feedback', href: '/use-cases/saas-product-feedback', internal: true }, { label: 'Documentation feedback', href: '/use-cases/documentation-feedback', internal: true }, { label: 'AI agent feedback', href: '/use-cases/ai-agent-chatbot-feedback', internal: true }, { label: 'HIPAA-compliant feedback', href: '/use-cases/hipaa-compliant-feedback', internal: true }, { label: 'All use cases', href: '/use-cases', internal: true }] },
              { title: 'Resources', links: [{ label: 'Guides', href: '/guides', internal: true }, { label: 'Compare tools', href: '/vs', internal: true }, { label: 'Alternatives', href: '/alternatives', internal: true }, { label: 'Documentation', href: '/docs', internal: true }, { label: 'Changelog', href: '/changelog', internal: true }] },
              { title: 'Company', links: [{ label: 'About', href: '/about', internal: true }, { label: 'BlueHive Health', href: 'https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_nav', external: true }, { label: 'Privacy Policy', href: '/privacy', internal: true }, { label: 'Terms of Service', href: '/terms', internal: true }, { label: 'Contact', href: 'mailto:support@pollenate.dev' }] },
            ].map((section) => (
              <div key={section.title}>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm uppercase tracking-wider">{section.title}</h4>
                <ul className="space-y-2.5 text-sm">
                  {section.links.map((link) =>
                    'internal' in link && link.internal ? (
                      <li key={link.label}><Link to={link.href} className="text-slate-500 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors">{link.label}</Link></li>
                    ) : (
                      <li key={link.label}><a href={link.href} {...('external' in link && link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="text-slate-500 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors">{link.label}</a></li>
                    )
                  )}
                </ul>
              </div>
            ))}
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200/60 dark:border-neutral-800/60 flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400 dark:text-neutral-500">&copy; 2026 Pollenate by <a href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">BlueHive Health</a>. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <a href="https://x.com/PollenateCloud" target="_blank" rel="noopener noreferrer" aria-label="Follow Pollenate on X (Twitter)" className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <a href="https://linkedin.com/company/pollenate-feedback/" target="_blank" rel="noopener noreferrer" aria-label="Follow Pollenate on LinkedIn" className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
